import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  FilteringState,
  GroupingState,
  IntegratedGrouping,
  CustomPaging,
  SortingState,
  PagingState,
  DataTypeProvider,
  SummaryState,
  IntegratedSummary,
  IntegratedSelection,
  SelectionState,
} from "@devexpress/dx-react-grid";
import {
  ExportPanel,
  Grid,
  DragDropProvider,
  GroupingPanel,
  PagingPanel,
  TableFilterRow,
  TableGroupRow,
  TableSummaryRow,
  TableHeaderRow,
  Toolbar,
  TableSelection,
  VirtualTable,
  TableColumnResizing,
} from "@devexpress/dx-react-grid-material-ui";
import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { GridExporter } from "@devexpress/dx-react-grid-export";
import { makeStyles } from "@material-ui/core/styles";
import ProjectButton from "./ProjectButton";
import { dataService } from "../../../../Services/data/data.service";
import { toast } from "react-toastify";

import saveAs from "file-saver";
import { BadgeCheck, BadgeMinus, Trash2, Pencil, Anchor, Pin } from "lucide-react";

import { measurementsTableColumnExtensions, resultGroupingColumnExtensions, filteringStateColumnExtensions, resultColumns, controlTypes, externalApiTypes, externalApiEnumBehaviorTypes, valueTypeCodes } from "../../../../Config/constants";
import { getCurrentDateTimeString, formatDate, formatToTimeString } from "../../../../Utils/dateHelper";
import MeasurementEditDialog from "./MeasurementEditDialog";
import CustomTooltip from "../../../Shared/Tooltip";

// ─── Column resizing helpers ──────────────────────────────────────────────
const DEFAULT_COLUMN_WIDTH = 180;
const COL_WIDTH_STORAGE_PREFIX = "taplr.colWidths.";

const buildDefaultColumnWidths = (columns) =>
  (columns || []).map((c) => {
    const ext = measurementsTableColumnExtensions.find((e) => e.columnName === c.name);
    return { columnName: c.name, width: (ext && ext.width) || DEFAULT_COLUMN_WIDTH };
  });

const loadStoredColumnWidths = (tableId) => {
  if (!tableId) return null;
  try {
    const raw = localStorage.getItem(COL_WIDTH_STORAGE_PREFIX + tableId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export default function MeasurementsTable(props) {
  const { t } = useTranslation();
  const [grouping, setGrouping] = useState([]);
  const [data, setData] = useState([]);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [image, setImage] = useState("");
  const [description, setDescription] = useState("");
  const [measurementToEdit, setMeasurementToEdit] = useState(null);

  // Column widths (resizable). Seeded from localStorage when props.tableId is provided.
  const [columnWidths, setColumnWidths] = useState(() => {
    const defaults = buildDefaultColumnWidths(props.columns);
    const stored = loadStoredColumnWidths(props.tableId);
    if (!stored) return defaults;
    return defaults.map((d) => {
      const s = stored.find((x) => x.columnName === d.columnName);
      return s && typeof s.width === "number" ? { columnName: d.columnName, width: s.width } : d;
    });
  });
  const persistWidthsTimeoutRef = useRef(null);

  // Keep columnWidths in sync when the set of columns changes (add/remove).
  useEffect(() => {
    setColumnWidths((prev) => {
      const defaults = buildDefaultColumnWidths(props.columns);
      return defaults.map((d) => {
        const existing = prev.find((p) => p.columnName === d.columnName);
        return existing ? { columnName: d.columnName, width: existing.width } : d;
      });
    });
  }, [props.columns]);

  const handleColumnWidthsChange = (next) => {
    setColumnWidths(next);
    if (!props.tableId) return;
    if (persistWidthsTimeoutRef.current) clearTimeout(persistWidthsTimeoutRef.current);
    persistWidthsTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(COL_WIDTH_STORAGE_PREFIX + props.tableId, JSON.stringify(next));
      } catch {
        /* ignore quota / serialization errors */
      }
    }, 250);
  };

  useEffect(() => {
    setData(props.data);
  }, [props.data]);

  const classes = useStyles();

  const exporterRef = useRef(null);

  // ── Export with selectAllMode support ────────────────────────────────────
  // Use a ref (not state) for export rows so the value persists across renders
  // through the entire async export pipeline without being cleared too early.
  const exportRowsRef = useRef(null);
  const shouldTriggerExportRef = useRef(false);
  const [exportTrigger, setExportTrigger] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const onSave = useCallback((workbook) => {
    const fileName = "registrations_" + getCurrentDateTimeString() + ".xlsx";
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
      // Clear export rows only after the file has been fully written & saved
      exportRowsRef.current = null;
      setExportTrigger(n => n + 1);
    });
  }, []);

  const startExport = useCallback(async (options) => {
    if (props.selectAllMode && props.fetchAllForExport) {
      setIsExporting(true);
      try {
        const allRows = await props.fetchAllForExport();
        exportRowsRef.current = allRows;
        shouldTriggerExportRef.current = true;
        setExportTrigger(n => n + 1); // triggers re-render with new rows, then useEffect
      } finally {
        setIsExporting(false);
      }
    } else {
      exporterRef.current.exportGrid(options);
    }
  }, [props.selectAllMode, props.fetchAllForExport]);

  // Fires after the render where GridExporter.rows = exportRowsRef.current (all rows).
  // Force selectedOnly:false — the fetched rows ARE the "selected" set.
  useEffect(() => {
    if (shouldTriggerExportRef.current && exportRowsRef.current) {
      shouldTriggerExportRef.current = false;
      exporterRef.current.exportGrid({ selectedOnly: false });
    }
  }, [exportTrigger]);

  const exportSelection = props.selection;

  const tableMessages = {
    sum: t('table.sum'),
    contains: t('table.contains'),
    noData: t('devExDataTable.noMeasurements'),
  };

  const columnTitleKeyMap = {
    companyName: 'tableResults.customer',
    projectName: 'tableResults.projectName',
    resourceName: 'tableResults.registrationPoint',
    description: 'common.description',
    valueType: 'tableResults.valueType',
    measurementValue: 'common.value',
    signedEventPrice: 'tableResults.eventPrice',
    keyValue: 'tableResults.keyValue',
    commentText: 'tableResults.comment',
    startHour: 'tableResults.startHour',
    startDayOfWeek: 'tableResults.startDayOfWeek',
    durationInMinutes: 'tableResults.durationInMinutes',
    startTime: 'tableResults.startTime',
    sessionValues: 'tableResults.sessionValues',
    running: 'tableResults.finished',
    action: 'tableResults.action',
  };

  const translatedColumns = React.useMemo(
    () =>
      (props.columns || []).map((column) => ({
        ...column,
        title: columnTitleKeyMap[column.name]
          ? t(columnTitleKeyMap[column.name], { defaultValue: column.title })
          : column.title || column.name,
      })),
    [props.columns, t]
  );

  const startTimeFormatter = (row) => (
    <React.Fragment>
      {row.value !== null && row.value !== "" ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ lineHeight: "1" }}>{formatDate(row.value).split(" ")[0]}</div>
          <div style={{ lineHeight: "1" }}>{formatDate(row.value).split(" ")[1]}</div>
        </div>
      ) : (
        ""
      )}
    </React.Fragment>
  );

  const finishedFormatter = (row) => <React.Fragment>{row.value ? <BadgeMinus style={{ color: "red" }}></BadgeMinus> : <BadgeCheck style={{ color: "green" }}></BadgeCheck>}</React.Fragment>;

  const valueTypeFormatter = (row) => {
    const valueTypeId = row.row?.valueTypeId;
    switch (valueTypeId) {
      case valueTypeCodes.VA:
        return <div className={classes.chip__green}>{t('devExDataTable.valueAdded')}</div>;
      case valueTypeCodes.NVA:
        return <div className={classes.chip__orange}>{t('devExDataTable.noValueAdded')}</div>;
      case valueTypeCodes.W:
        return <div className={classes.chip__red}>{t('devExDataTable.waste')}</div>;
      default:
        return <div className={classes.chip__grey}>{t('devExDataTable.unknown')}</div>;
    }
  };

  const valueFormatter = (row) => {
    if (row.row !== undefined) {
      if (row.column.name === "commentText" && row.row !== undefined && row.row.commentImage !== "" && row.row.commentImage !== null && row.row.commentImage !== undefined) {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {row.value !== null && row.value !== undefined && row.value !== "" && <span>{row.value}</span>}
            <IconButton style={{ borderRadius: "25px" }} onClick={() => handleDialogOpen(row.row.imageId, row.row.description, row.row.commentImage)}>
              <img alt="" style={{ maxWidth: "100%" }} src={"data:image/png;base64," + row.row.commentImage}></img>
            </IconButton>
          </div>
        );
      }
      if (row.value === null || row.value === undefined || row.value === "") {
        if (row.column.name === "measurementValue" && row.row.typeId === controlTypes.STOPWATCH && row.row.integerValue) {
          const totalSeconds = row.row.integerValue;

          // Calculate hours, minutes, and seconds
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;

          // Format each part to ensure two digits
          const formattedTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
          return formattedTime;
        } else {
          return "-";
        }
      } else if (row.row.typeId === controlTypes.CAMERA) {
        return (
          <IconButton style={{ borderRadius: "25px" }} onClick={() => handleDialogOpen(row.row.imageId, row.row.description)}>
            <img alt="" style={{ maxWidth: "100%" }} src={"data:image/png;base64," + row.row.thumbnail}></img>
          </IconButton>
        );
      } else {
        return row.value;
      }
    } else {
      if (row.value === null || row.value === undefined || row.value === "") {
        return "-";
      } else {
        return row.value;
      }
    }
  };

  const sessionValuesFormatter = (row) => {
    if (!row.value) return "-";
    const metadata = row.row?.sessionMetadata || [];
    return (
      <CustomTooltip
        anchorTo="td"
        content={
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {metadata.map((m, i) => (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {m.behaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR && <Anchor size={14} />}
                {m.behaviorTypeId === externalApiEnumBehaviorTypes.STICKY && <Pin size={14} />}
                {m.value}
              </span>
            ))}
          </div>
        }
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{row.value}</span>
      </CustomTooltip>
    );
  };

  const priceFormatter = (row) => {
    if (row.value === null || row.value === undefined || row.value === "") {
      return "-";
    } else {
      const formatPrice = (price) => {
        const numericPrice = parseFloat(price) || 0;
        return numericPrice
          .toFixed(2) // Ensure two decimal places
          .replace(".", ",") // Replace decimal point with comma
          .replace(/\B(?=(\d{3})+(?!\d))/g, " "); // Add space as thousands separator
      };

      return <p className={classes.eventPrice}>€{formatPrice(row.value)}</p>;
    }
  };

  const handleDeleteRecord = (measurementId) => {
    setMeasurementToEdit(null);
    props.handleDeleteMeasurement(measurementId);
  };

  const actionFormatter = (row) => {
    return (
      // <div className={classes.buttonContainer}>
      //     <button className={classes.iconButton} onClick={() => handleDeleteRecord(row.row.id)}>
      //         <Info className={classes.iconButton__icon} />
      //     </button>
      //     {props.Login.isTaplrAdmin ?
      //         <button className={classes.iconButton} onClick={() => handleDeleteRecord(row.row.id)}>
      //             <Pencil className={classes.iconButton__icon} />
      //         </button>
      //         :
      //         <button className={classes.iconButton} onClick={() => handleDeleteRecord(row.row.id)}>
      //             <Trash2 className={classes.iconButton__icon} />
      //         </button>
      //     }
      // </div>
      <>
        <ProjectButton data={row.row} details={data.filter((x) => x.projectId === row.row.projectId)} />
        <Tooltip title={props.Login.isTaplrAdmin ? t('measurementsTable.editMeasurement') : t('measurementsTable.deleteMeasurement')} placement="top" disableInteractive arrow>
          {props.Login.isTaplrAdmin ? (
            <IconButton style={{ borderRadius: 0 }} onClick={() => setMeasurementToEdit(row.row)}>
              <Pencil />
            </IconButton>
          ) : (
            <IconButton style={{ borderRadius: 0 }} onClick={() => handleDeleteRecord(row.row.id)}>
              <Trash2 />
            </IconButton>
          )}
        </Tooltip>
      </>
    );
  };

  const handleDialogOpen = (imageId, description, commentImage) => {
    if (imageId !== null && imageId !== undefined && imageId !== 0) {
      dataService
        .GetImageById(imageId)
        .then((response) => {
          setImage(response.image);
          setDescription(description);
          setImageDialogOpen(true);
        })
        .catch((error) => {
          toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        });
    } else if (commentImage !== "" && commentImage !== undefined && commentImage !== null) {
      setImage(commentImage);
      setDescription(description);
      setImageDialogOpen(true);
    } else {
      toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    }
  };

  const CustomGroupRowContent = ({ row, column, children }) => {
    var filterData = JSON.parse(JSON.stringify(data));
    const summary = row.value;
    var count = 0;
    var counterMessage = "";
    var timerMessage = "";
    const key = grouping.findIndex((item) => item.columnName === row.groupedBy);
    var arrCompoundKey = row.compoundKey.split("|");
    var groupBySubLevel = [];
    if (key > 0) {
      //sub level
      grouping.forEach((x, index) => {
        groupBySubLevel = groupByColumnName(filterData, x.columnName);
        filterData = groupBySubLevel[arrCompoundKey[index]];
      });
    }

    const groupedRows = filterData.filter((r) => r[row.groupedBy] === row.value);
    if (groupedRows !== undefined) {
      count = groupedRows.length;
      var oldCounterRows = groupedRows.filter((x) => x.typeId === controlTypes.COUNTER);
      let counterRows = groupedRows.filter((x) => x.typeId === controlTypes.EXTERNALAPI && x.externalApiTypeId === externalApiTypes.NUMERIC);
      var timerRows = groupedRows.filter((x) => x.typeId === controlTypes.STOPWATCH);
      if (oldCounterRows.length > 0 || counterRows.length > 0) {
        var total = 0;
        let counterTotal = 0;
        let oldCounterTotal = 0;

        counterRows.forEach((x) => {
          if (x.isNegative) {
            counterTotal -= x.measurementValue;
          } else {
            counterTotal += x.measurementValue;
          }
        });

        let positiveCounterRows = oldCounterRows.filter((x) => !x.isNegative);
        let negativeCounterRows = oldCounterRows.filter((x) => x.isNegative);

        oldCounterTotal = positiveCounterRows.length - negativeCounterRows.length;
        total = counterTotal + oldCounterTotal;
        counterMessage = total; // Use the formatted message
      }

      if (timerRows.length > 0) {
        let total = 0;
        timerRows.forEach((x) => {
          total = total + x.integerValue;
        });
        timerMessage = formatToTimeString(total);
      }
    }

    return (
      <TableGroupRow.Content row={row} column={column} sx={{ width: "100%", display: "flex", flexDirection: "row" }}>
        {/* {children} */}
        {/* Each stat has a fixed width (so a given stat is the same size in every group) and clips
            with an ellipsis; the row scrolls horizontally when the stats don't fit instead of overlapping. */}
        <div
          style={{
            width: "100%",
            minWidth: 0,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "20px",
            paddingLeft: "5px",
            paddingBottom: "2px",
            flexWrap: "nowrap",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <div style={{ minWidth: "200px", flex: "1 1 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {children !== null ? children : summary}
          </div>
          <span style={{ minWidth: "170px", flex: "1 1 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            <b>{t('devExDataTable.counterTotal')}:</b> {counterMessage ? counterMessage : "-"}
          </span>
          <span style={{ minWidth: "250px", flex: "1 1 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            <b>{t('devExDataTable.totalTime')}:</b> {timerMessage ? timerMessage : "-"}
          </span>
          <span style={{ minWidth: "150px", flex: "1 1 0", display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
            <b>{t('devExDataTable.count')}:</b> <span style={{ minWidth: "30px", display: "flex" }}>{count}</span>
          </span>
        </div>
      </TableGroupRow.Content>
    );
  };

  const groupByColumnName = (array, columnName) => {
    //Function to group all measurments by the given columnName
    return array.reduce((result, item) => {
      const key = item[columnName]; // Dynamically get the key value
      if (!result[key]) {
        result[key] = []; // Initialize the group if it doesn't exist
      }
      result[key].push(item); // Add the current item to the group
      return result;
    }, {});
  };

  const handleDialogClose = () => {
    setImageDialogOpen(false);
  };

  const handleCloseEditDialog = () => {
    setMeasurementToEdit(null);
  };

  const formatlessSummaryTypes = ["customTotal"];
  const messages = {
    customTotal: t('devExDataTable.priceHigherThan6000'),
    count: t('devExDataTable.count'),
  };

  return (
    <React.Fragment>
      <div id="taplrDevExTable" style={{ height: "100%" }}>
        <Grid rows={data} columns={translatedColumns} height="100%">
          <DragDropProvider />

          {/* DataTypeProviders are used to format the data, like dates or the valuetype (we show these as a pill) */}
          <DataTypeProvider formatterComponent={startTimeFormatter} for={["startTime"]} />
          <DataTypeProvider formatterComponent={finishedFormatter} for={["running"]} />
          <DataTypeProvider formatterComponent={valueFormatter} for={["measurementValue", "keyValue", "commentText"]} />
          <DataTypeProvider formatterComponent={valueTypeFormatter} for={["valueType"]} />
          <DataTypeProvider formatterComponent={priceFormatter} for={["signedEventPrice"]} />
          <DataTypeProvider formatterComponent={sessionValuesFormatter} for={["sessionValues"]} />
          <DataTypeProvider formatterComponent={actionFormatter} for={["action"]} />

          <FilteringState
            filters={props.serverFilters}
            onFiltersChange={props.onFiltersChange}
            columnExtensions={filteringStateColumnExtensions}
          />
          <SortingState
            sorting={props.sorting}
            onSortingChange={props.onSortingChange}
            columnExtensions={measurementsTableColumnExtensions}
          />
          <GroupingState grouping={grouping} onGroupingChange={setGrouping} columnExtensions={resultGroupingColumnExtensions} showGroupingControls />
          <SummaryState />
          <PagingState
            currentPage={props.currentPage}
            onCurrentPageChange={props.onCurrentPageChange}
            pageSize={props.pageSize}
            onPageSizeChange={props.onPageSizeChange}
          />
          <SelectionState selection={props.selection} onSelectionChange={props.onSelectionChange} mode="multiple" deferred={true} />

          <CustomPaging totalCount={props.totalCount} />
          <IntegratedGrouping />
          <IntegratedSummary messages={messages} />
          <IntegratedSelection getRowId={(row) => row.id} />

          <VirtualTable columnExtensions={measurementsTableColumnExtensions} messages={tableMessages} />
          <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={handleColumnWidthsChange} resizingMode="widget" />
          <TableFilterRow showFilterSelector={false} />
          <TableHeaderRow showSortingControls columnExtensions={measurementsTableColumnExtensions} />
          <TableSelection showSelectAll enabled={true} rowSelectionEnabled={true} />
          <TableGroupRow contentComponent={CustomGroupRowContent} showColumnsWhenGrouped />

          <TableSummaryRow formatlessSummaryTypes={formatlessSummaryTypes} />

          <Toolbar />
          <GroupingPanel showSortingControls={true} showGroupingControls></GroupingPanel>

          <PagingPanel
            pageSizes={[50, 100, 200, 500]}
            messages={{
              showAll: t('devExDataTable.showAll'),
              rowsPerPage: t('table.rowsPerPage'),
              info: t('devExDataTable.pagingInfo'),
            }}
          />
          <ExportPanel startExport={startExport} />
        </Grid>
        <GridExporter
          ref={exporterRef}
          rows={exportRowsRef.current || data}
          columns={resultColumns}
          grouping={grouping}
          selection={exportSelection}
          onSave={onSave}
        />
      </div>
      {imageDialogOpen && (
        <Dialog open={imageDialogOpen} onClose={handleDialogClose}>
          <DialogTitle>{description}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <img alt="" style={{ maxWidth: "100%", maxHeight: "calc(100vh - 64px)" }} src={"data:image/png;base64," + image}></img>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ padding: "0px 24px 10px 0px" }}>
            <Button autoFocus content="Close" className={classes.closeButton} onClick={handleDialogClose}>
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <MeasurementEditDialog
        dialogOpen={measurementToEdit}
        closeDialog={handleCloseEditDialog}
        measurementToEdit={measurementToEdit}
        setMeasurementFinished={props.setMeasurementFinished}
        handleDeleteRecord={handleDeleteRecord}
        {...props}
      />
    </React.Fragment>
  );
}

const useStyles = makeStyles(() => ({
  valueStyle: {
    whiteSpace: "wrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    maxWidth: "250px",
  },
  closeButton: {
    gap: "8px",
    backgroundColor: "#0B4097 !important",
    border: "1px solid #0B4097 !important",
    color: "white !important",
    "&:hover": {
      background: "white !important",
      color: "#0B4097 !important",
    },
    "&:disabled": {
      background: "#bdb6ac !important",
      border: "1px solid grey !important",
      color: "white !important",
      cursor: "not-allowed",
    },
  },
  tableFooter: {
    width: "100%",
  },
  footer: {
    display: "flex",
  },
  tableHeadRow: {},
  tableHead: {
    height: "42px",
    borderRight: "2px solid #e6ecf0",
  },
  titleSearch: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  icons: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  imageStyle: {
    marginTop: "15px",
    height: "100px",
    width: "100px",
  },

  chip__grey: {
    width: "fit-content",
    height: "24px",
    display: "inline-flex",
    alignItems: "center",
    padding: "0px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    backgroundColor: "#EDEDED",
    color: "#7C7C7C",
  },
  chip__green: {
    width: "fit-content",
    height: "24px",
    display: "inline-flex",
    alignItems: "center",
    padding: "0px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    backgroundColor: "#E2F8E0",
    color: "#3AB037",
  },
  chip__orange: {
    width: "fit-content",
    height: "24px",
    display: "inline-flex",
    alignItems: "center",
    padding: "0px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    backgroundColor: "#FEF1D6",
    color: "#E76F0F",
  },

  chip__red: {
    width: "fit-content",
    height: "24px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    backgroundColor: "#FBE5E5",
    color: "#D84949",
  },

  eventPrice: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 1)",
    margin: "0px",
    textAlign: "right",
  },

  emptyStateCard: {
    maxWidth: "320px",
    boxSizing: "border-box",
    padding: "16px",
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "transparent",
  },

  emptyStateCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",
    margin: "0px",
    marginBottom: "8px",
  },

  emptyStateCard__subtitle: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 0.8)",
    textAlign: "center",
    margin: "0px",
  },

  buttonContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "transparent",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },
}));
